import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Sparkles, Star, MapPin, FileText, Plus } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

function ProCard({ p, onClick }) {
  return (
    <button onClick={onClick} data-testid={`pro-card-${p.user_id}`}
      className="text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 hover:border-amber-500/50 transition-colors flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold truncate">{p.name || "Professionnel"}</span>
        {p.rating != null && <span className="flex items-center gap-1 text-xs text-amber-400 shrink-0"><Star className="w-3.5 h-3.5 fill-amber-400" />{p.rating} ({p.reviews_count})</span>}
      </div>
      {p.headline && <p className="text-sm text-zinc-300 line-clamp-2">{p.headline}</p>}
      <div className="flex flex-wrap gap-1.5">
        {(p.categories || []).slice(0, 3).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-400">{c}</span>)}
      </div>
      {(p.city || p.country) && <p className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{[p.city, p.country].filter(Boolean).join(", ")}</p>}
      {p.match_reason && <p className="text-xs text-amber-300/90 bg-amber-500/10 rounded-lg px-2 py-1 mt-1">✦ {p.match_reason}</p>}
    </button>
  );
}

export default function Marketplace() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("ai"); // ai | browse
  const [query, setQuery] = useState("");
  const [aiRes, setAiRes] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [list, setList] = useState(null);

  useEffect(() => { api.get("/marketplace/categories").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);

  const browse = useCallback(async () => {
    const p = new URLSearchParams({ search, category, location });
    try { setList((await api.get(`/marketplace/professionals?${p}`)).data.professionals); } catch { setList([]); }
  }, [search, category, location]);
  useEffect(() => { if (tab === "browse") browse(); }, [tab, browse]);

  const runAi = async () => {
    if (!query.trim()) return;
    setAiLoading(true); setAiRes(null);
    try {
      const { data } = await api.post("/marketplace/match", { query });
      setAiRes(data);
    } catch { toast.error("Recherche indisponible, réessaie."); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">Marché Cehnura</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Trouve le bon professionnel</h1>
        <p className="text-zinc-500 mt-2">Décris ton besoin en langage naturel, ou explore par catégorie.</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("ai")} data-testid="mkt-tab-ai" className={`h-10 px-4 rounded-xl text-sm border ${tab === "ai" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}><Sparkles className="w-4 h-4 inline mr-1.5" />Recherche IA</button>
        <button onClick={() => setTab("browse")} data-testid="mkt-tab-browse" className={`h-10 px-4 rounded-xl text-sm border ${tab === "browse" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}><Search className="w-4 h-4 inline mr-1.5" />Explorer</button>
        <button onClick={() => setTab("requests")} data-testid="mkt-tab-requests" className={`h-10 px-4 rounded-xl text-sm border ${tab === "requests" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}><FileText className="w-4 h-4 inline mr-1.5" />Demandes</button>
      </div>

      {tab === "ai" && (
        <div className="space-y-4">
          <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4">
            <textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={3} data-testid="mkt-ai-query"
              placeholder="Ex : Je veux créer un site internet pour mon entreprise / réaliser un clip de rap / faire un shooting photo…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-amber-500/50 resize-none" />
            <button onClick={runAi} disabled={aiLoading} data-testid="mkt-ai-submit"
              className="mt-3 h-11 px-5 rounded-xl gold-gradient text-black font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {aiLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5" /> Trouver</>}
            </button>
          </div>
          {aiRes && (
            <>
              {aiRes.need && (
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-sm" data-testid="mkt-ai-need">
                  <p className="text-zinc-300"><span className="text-amber-400 font-semibold">Besoin identifié :</span> {aiRes.need.summary}</p>
                  {(aiRes.need.roles || []).length > 0 && <p className="text-zinc-400 mt-1">Profils recherchés : {(aiRes.need.roles).join(", ")}</p>}
                  {(aiRes.need.categories || []).length > 0 && <p className="text-zinc-500 mt-1 text-xs">Catégories : {aiRes.need.categories.join(", ")}</p>}
                </div>
              )}
              <ResultsGrid items={aiRes.results} navigate={navigate} empty="Aucun professionnel ne correspond encore. Invite des pros à rejoindre Cehnura !" />
            </>
          )}
        </div>
      )}
      {tab === "browse" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mkt-search" placeholder="Mot-clé, spécialité…"
                className="w-full h-11 pl-9 pr-3 bg-[#0F0F13] border border-zinc-800 rounded-xl text-sm text-zinc-200 outline-none focus:border-amber-500/50" />
            </div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="mkt-category"
              className="h-11 px-3 bg-[#0F0F13] border border-zinc-800 rounded-xl text-sm text-zinc-200">
              <option value="">Toutes catégories</option>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="mkt-location" placeholder="Localisation"
              className="h-11 px-3 bg-[#0F0F13] border border-zinc-800 rounded-xl text-sm text-zinc-200 outline-none focus:border-amber-500/50" />
          </div>
          <ResultsGrid items={list} navigate={navigate} empty="Aucun professionnel pour ces filtres." />
        </div>
      )}
      {tab === "requests" && <RequestsPanel navigate={navigate} cats={cats} />}
    </div>
  );
}

function ResultsGrid({ items, navigate, empty }) {
  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  if (items.length === 0) return <div className="text-center py-14 text-zinc-500 text-sm" data-testid="mkt-empty">{empty}</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mkt-results">
      {items.map((p) => <ProCard key={p.user_id} p={p} onClick={() => navigate(`/marketplace/${p.user_id}`)} />)}
    </div>
  );
}

function RequestsPanel({ navigate, cats }) {
  const [mine, setMine] = useState(false);
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ title: "", description: "", category: "", skills: "", budget: "", location: "", timing: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const load = useCallback(async () => {
    const p = new URLSearchParams({ mine: mine ? "1" : "" });
    try { setList((await api.get(`/marketplace/requests?${p}`)).data.requests); } catch { setList([]); }
  }, [mine]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.title.trim()) return;
    try {
      await api.post("/marketplace/requests", { ...f, skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean), budget: f.budget === "" ? null : Number(f.budget) });
      toast.success("Demande publiée"); setShowForm(false);
      setF({ title: "", description: "", category: "", skills: "", budget: "", location: "", timing: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  return (
    <div className="space-y-4" data-testid="mkt-requests">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setShowForm((v) => !v)} data-testid="req-create-toggle" className="h-10 px-4 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center gap-1.5"><Plus className="w-4 h-4" />Publier une demande</button>
        <button onClick={() => setMine((v) => !v)} data-testid="req-mine-toggle" className={`h-10 px-4 rounded-xl text-sm border ${mine ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>Mes demandes</button>
      </div>
      {showForm && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 space-y-3" data-testid="req-form">
          <input value={f.title} onChange={set("title")} data-testid="req-title" placeholder="Titre (ex : Créer mon application mobile)" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" />
          <textarea value={f.description} onChange={set("description")} data-testid="req-desc" rows={2} placeholder="Décris ton projet" className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 resize-none" />
          <div className="grid sm:grid-cols-2 gap-2">
            <select value={f.category} onChange={set("category")} data-testid="req-category" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-200">
              <option value="">Catégorie</option>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input value={f.skills} onChange={set("skills")} data-testid="req-skills" placeholder="Compétences (virgules)" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" />
            <input value={f.budget} onChange={set("budget")} data-testid="req-budget" placeholder="Budget €" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" />
            <input value={f.location} onChange={set("location")} data-testid="req-location" placeholder="Localisation" className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" />
          </div>
          <button onClick={create} data-testid="req-submit" className="h-10 px-5 rounded-xl gold-gradient text-black font-semibold text-sm">Publier</button>
        </div>
      )}
      {list === null ? <div className="flex justify-center py-14"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div> :
        list.length === 0 ? <div className="text-center py-12 text-zinc-500 text-sm" data-testid="req-empty">Aucune demande pour le moment.</div> : (
          <div className="space-y-2">
            {list.map((r) => (
              <button key={r.id} onClick={() => navigate(`/marketplace/requests/${r.id}`)} data-testid={`req-card-${r.id}`}
                className="w-full text-left bg-[#18181E] border border-zinc-800 rounded-2xl p-4 hover:border-amber-500/50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold truncate">{r.title}</span>
                  <span className="text-xs text-zinc-500 shrink-0">{r.proposals_count} proposition(s)</span>
                </div>
                {r.description && <p className="text-sm text-zinc-400 line-clamp-2 mt-1">{r.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {r.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">{r.category}</span>}
                  {(r.skills || []).slice(0, 4).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-400">{s}</span>)}
                  {r.status === "CLOSED" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">Fermée</span>}
                </div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}
